import React, { useState, useEffect, useRef } from 'react';
import {
  handleLoginFlow,
  getGoogleAuthUrl,
  collectAndStoreUTMData,
  submitEmailOnly,
  APIResponse,
  resolveOauthPath,
  resolveLandingTarget,
  getExternalClickIds,
  LP_SOURCE,
} from '../../utils/api';
import { getEfTransactionId } from '../../utils/everflow';
import { getStorage } from '../../utils/storage';
import {
  useFormValidation,
  validationRules,
} from '../../hooks/useFormValidation';
import { useGrowthbook } from '../../hooks/useGrowthbook';
import GoogleIcon from '../../assets/GoogleIcon';
import OtpStep from './OtpStep';
import { AuthRedirectIntent } from '../../types';
import { trackLoginCompleted } from '../../utils/analytics';

interface LoginAccountProps {
  onSwitchToSignup?: () => void;
  onSwitchToForgot?: () => void;
  onClose?: () => void;
  redirectIntent: AuthRedirectIntent;
}

const LoginAccount: React.FC<LoginAccountProps> = ({
  onSwitchToSignup,
  onSwitchToForgot,
  redirectIntent,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otpState, setOtpState] = useState<{ email: string; otpExpiry?: string } | null>(null);

  const { features } = useGrowthbook();
  const isMagicLink = features.magicLink === 'ON';

  // GrowthBook variants forwarded with every auth request (and the Google OAuth
  // state) so the backend can attribute per A/B variant — mirrors create-friend's
  // useGBValue `ab_testing`. Previously an empty object, so `lander-content` +
  // `create-ai-slut-magicLink` were missing when auth events fired.
  const ab_testing = {
    'lander-content': features.name || 'none',
    'create-ai-slut-magicLink': features.magicLink,
  };

  const googleBtnRef = useRef<HTMLAnchorElement>(null);

  // Use the custom validation hook
  const { values, validateAll, getFieldProps } = useFormValidation(
    { email: '', password: '' },
    {
      email: validationRules.email,
      password: validationRules.password,
    },
  );

  useEffect(() => {
    const utmObj = collectAndStoreUTMData();
    let cancelled = false;
    (async () => {
      const efTransactionId = getEfTransactionId();
      const externalClickIds = await getExternalClickIds();
      if (cancelled || !googleBtnRef.current) return;
      // See CreateAccount.tsx — backend reads `path` from OAuth state to build
      // `${frontend}${path}` post-OAuth.
      const oauthPath = resolveOauthPath(redirectIntent);
      const finalUtmObj = {
        ...utmObj,
        ab_testing: JSON.stringify(ab_testing),
        lp_source: LP_SOURCE,
        externalClickIds,
        ...(efTransactionId ? { ef_transaction_id: efTransactionId } : {}),
        path: oauthPath,
      };
      const googleAuthUrl = getGoogleAuthUrl();
      googleBtnRef.current.href = `${googleAuthUrl}?state=${encodeURIComponent(
        JSON.stringify(finalUtmObj),
      )}`;
      googleBtnRef.current.onclick = () => {
        localStorage.setItem('googleAuthInProgress', 'true');
        localStorage.setItem('googleAuthStartTime', Date.now().toString());
      };
    })();
    return () => {
      cancelled = true;
    };
  }, [redirectIntent]);

  // Removed handleAuthCallback as it's not needed for basic functionality

  const goToPaywall = (authToken: string) => {
    const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;

    // Honor the same landing target as the password flow (handleLoginFlow) so
    // the magic-link / OTP paths carry selected_slug (chat) or route to
    // /create-ai. Without it the paywall has no redirect target → home.
    const target = resolveLandingTarget(redirectIntent);
    const destinationUrl = new URL(`${SWIPEY_BASE_URL}${target.basePath}`);

    const utm = (getStorage('utm_data') as Record<string, string> | null) || {};
    Object.entries(utm).forEach(([key, value]) => {
      if (value) destinationUrl.searchParams.set(key, value);
    });

    Object.entries(target.params).forEach(([key, value]) =>
      destinationUrl.searchParams.set(key, value),
    );

    if (target.kind === 'paywall') {
      destinationUrl.searchParams.set('type', 'realistic');
      destinationUrl.searchParams.set('from_landing', 'true');
    }

    destinationUrl.searchParams.set('auth_token', authToken);
    destinationUrl.searchParams.set('lp_source', LP_SOURCE);
    destinationUrl.searchParams.set(
      'auth_type',
      target.kind === 'paywall' ? 'login_paywall' : 'login_pre_lander',
    );

    const efTransactionId = getEfTransactionId();
    if (efTransactionId) {
      destinationUrl.searchParams.set('ef_transaction_id', efTransactionId);
    } else {
      destinationUrl.searchParams.delete('ef_transaction_id');
    }

    localStorage.setItem('isCompleted', 'true');
    window.location.href = destinationUrl.toString();
  };

  const handleEmailOnlySubmit = async () => {
    const emailErr = validationRules.email(values.email);
    if (emailErr) {
      setError(emailErr);
      return;
    }
    setIsLoading(true);
    setError('');

    try {
      const data = await submitEmailOnly(values.email, { ab_testing });

      if (data?.success === false) {
        setError(data.message || 'Please check your email');
        return;
      }

      if (data?.otp_sent) {
        setOtpState({ email: values.email, otpExpiry: data.otpExpiry });
        return;
      }

      // Prefer the token-built destination (carries selected_slug / create-ai)
      // over the backend loginUrl, which is a bare /paywall → home after payment.
      const token = data?.authToken || data?.token;
      if (token) {
        trackLoginCompleted();
        goToPaywall(token);
        return;
      }

      if (data?.redirectUrl || data?.loginUrl) {
        trackLoginCompleted();
        localStorage.setItem('isCompleted', 'true');
        window.location.href = (data.redirectUrl || data.loginUrl) as string;
        return;
      }

      setError(data?.message || 'Please check your email');
    } catch (err: any) {
      setError(err?.message || 'Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = (data: APIResponse) => {
    trackLoginCompleted();
    localStorage.setItem('isCompleted', 'true');
    // Prefer the token-built destination (carries selected_slug / create-ai)
    // over the backend loginUrl, which is a bare /paywall → home after payment.
    const token = data?.authToken || data?.token;
    if (token) {
      goToPaywall(token);
      return;
    }
    if (data?.redirectUrl || data?.loginUrl) {
      window.location.href = (data.redirectUrl || data.loginUrl) as string;
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isMagicLink) {
      await handleEmailOnlySubmit();
      return;
    }

    // Validate all fields
    if (!validateAll()) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await handleLoginFlow(
        values.email,
        values.password,
        ab_testing,
        redirectIntent,
      );

      if (result.success && result.redirectUrl) {
        trackLoginCompleted();
        localStorage.setItem('isCompleted', 'true');
        window.location.href = result.redirectUrl;
      } else {
        setError(result.message || 'Please check your email and password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Magic-link variant: tighter inputs (44px, radius 12, white/12% bg, white/24% border).
  // Default variant: classic 46px height, radius 18, white border.
  const baseInputClass = isMagicLink
    ? 'rounded-[12px] border border-white/[0.24] bg-white/[0.12] px-4 py-2 text-white font-poppins text-base font-normal leading-[120%] w-full h-[44px] transition-all duration-300 placeholder:text-white focus:outline-none focus:border-white/40'
    : 'rounded-[18px] border border-white bg-white/10 px-4 py-4 text-white font-poppins text-base font-normal leading-[120%] w-full h-[46px] transition-all duration-300 placeholder:text-white focus:outline-none focus:border-[#fb6284] focus:bg-white/15';
  const errorInputClass = 'border-[#ff4444] shadow-[0_0_0_1px_rgba(255,68,68,0.2)]';

  // Get CSS classes for input styling
  const getEmailInputClass = () => {
    const emailProps = getFieldProps('email');
    if (emailProps.hasError) return `${baseInputClass} ${errorInputClass}`;
    return baseInputClass;
  };

  const getPasswordInputClass = () => {
    const passwordProps = getFieldProps('password');
    if (passwordProps.hasError) return `${baseInputClass} ${errorInputClass}`;
    return baseInputClass;
  };

  // Magic-link variant: dark translucent pill with white text.
  const googleBtnClass = isMagicLink
    ? "text-white font-poppins text-sm font-medium leading-[140%] tracking-[-0.14px] rounded-[26px] border border-white/10 bg-[radial-gradient(72.46%_2094.65%_at_8.56%_0%,rgba(244,235,255,0.16)_0%,rgba(235,235,255,0.06)_100%)] shadow-[0_0_20.78px_0_rgba(0,0,0,0.09),0_0_127.88px_0_rgba(0,0,0,0.45)] h-[40px] px-5 cursor-pointer transition-all duration-300 w-full flex items-center justify-center gap-[10px] no-underline hover:bg-[radial-gradient(72.46%_2094.65%_at_8.56%_0%,rgba(244,235,255,0.22)_0%,rgba(235,235,255,0.1)_100%)]"
    : "text-black font-poppins text-base font-semibold leading-[120%] capitalize rounded-full border border-[#8e918f] bg-white px-[15px] py-[15px] cursor-pointer transition-all duration-300 w-full flex items-center justify-center gap-[10px] mb-5 no-underline hover:bg-[#2a2a2a] hover:-translate-y-0.5 hover:text-white";

  if (otpState) {
    return (
      <OtpStep
        email={otpState.email}
        initialOtpExpiry={otpState.otpExpiry}
        onBack={() => setOtpState(null)}
        onVerified={handleOtpVerified}
        ab_testing={ab_testing}
      />
    );
  }

  return (
    <div className="w-full">
      <div className="w-full">
        <div className="w-full">
          <div className="text-center mb-[30px]">
            <h2 className="text-white text-center font-poppins text-[28px] font-semibold leading-[120%] capitalize mb-[10px]">
              Log In
            </h2>
            <p className="text-white font-poppins text-base font-semibold leading-[120%] capitalize mb-5 flex items-center justify-center gap-[3px]">
              to CreateAISlut
            </p>
            <button className="hidden">×</button>
          </div>

          <form
            onSubmit={handleLoginSubmit}
            className="w-full max-w-[330px] mx-auto"
          >
            <a
              ref={googleBtnRef}
              href="#"
              className={googleBtnClass}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z"
                  fill="#4285F4"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.99976 18C11.4298 18 13.467 17.1941 14.9561 15.8195L12.0475 13.5614C11.2416 14.1014 10.2107 14.4204 8.99976 14.4204C6.65567 14.4204 4.67158 12.8373 3.96385 10.71H0.957031V13.0418C2.43794 15.9832 5.48158 18 8.99976 18Z"
                  fill="#34A853"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 8.99999C3.68182 8.40681 3.78409 7.82999 3.96409 7.28999V4.95818H0.957273C0.347727 6.17318 0 7.54772 0 8.99999C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z"
                  fill="#FBBC05"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8.99976 3.57955C10.3211 3.57955 11.5075 4.03364 12.4402 4.92545L15.0216 2.34409C13.4629 0.891818 11.4257 0 8.99976 0C5.48158 0 2.43794 2.01682 0.957031 4.95818L3.96385 7.29C4.67158 5.16273 6.65567 3.57955 8.99976 3.57955Z"
                  fill="#EA4335"
                />
              </svg>
              {isMagicLink ? 'Google' : 'Sign in with Google'}
            </a>

              <div className="flex items-center gap-3 my-[10px] text-white/60">
                <span className="flex-1 border-t border-white/[0.12]" />
                <span className="flex-shrink-0">or</span>
                <span className="flex-1 border-t border-white/[0.12]" />
              </div>

            <div className="relative w-full pb-2">
              <input
                placeholder="Email"
                type="email"
                value={getFieldProps('email').value}
                onChange={getFieldProps('email').onChange}
                onBlur={getFieldProps('email').onBlur}
                className={getEmailInputClass()}
                aria-invalid={getFieldProps('email').hasError}
                aria-describedby={
                  getFieldProps('email').hasError
                    ? 'login-email-error'
                    : undefined
                }
                autoComplete="email"
              />
              {getFieldProps('email').hasError && (
                <div
                  id="login-email-error"
                  className="text-[#ff4444] font-poppins text-xs font-normal leading-[120%] -mt-1 pb-1 ml-1 text-start pt-[5px]"
                >
                  {getFieldProps('email').error}
                </div>
              )}
            </div>
            {!isMagicLink && (
            <div className="relative flex flex-col items-stretch w-full gap-1">
              <div className="relative w-full pb-2">
                <input
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={getFieldProps('password').value}
                  onChange={getFieldProps('password').onChange}
                  onBlur={getFieldProps('password').onBlur}
                  className={getPasswordInputClass()}
                  required
                  minLength={6}
                  aria-invalid={getFieldProps('password').hasError}
                  aria-describedby={
                    getFieldProps('password').hasError
                      ? 'login-password-error'
                      : undefined
                  }
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="absolute top-1/2 -translate-y-1/2 right-4 cursor-pointer bg-none border-none p-0"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z"
                        stroke="#9B9CAA"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
                        stroke="#9B9CAA"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421"
                        stroke="#9B9CAA"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {getFieldProps('password').hasError && (
                <div
                  id="login-password-error"
                  className="text-[#ff4444] font-poppins text-xs font-normal leading-[120%] -mt-1 pb-1 ml-1 text-start"
                >
                  {getFieldProps('password').error}
                </div>
              )}
            </div>
            )}

            {error && (
              <div className="text-[#ff4444] font-poppins text-sm font-normal leading-[120%] text-start -mt-1">
                {error}
              </div>
            )}

            {!isMagicLink && (
            <div
              onClick={onSwitchToForgot}
              className="text-white font-poppins text-xs font-normal leading-[120%] mt-2 mb-[18px] flex items-start justify-start cursor-pointer"
            >
              <span>Forgot password?</span>
            </div>
            )}

            <button
              type="submit"
              className="rounded-[18px] bg-gradient-to-r from-[#8350cb] to-[#fe6283] shadow-[0_4px_16.8px_0_rgba(240,73,243,0.47)] border-none px-[15px] py-[15px] text-white text-base font-semibold cursor-pointer transition-all duration-300 w-full mb-5 hover:bg-[#2a2a2a] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Log In'}
            </button>

            <div className="text-[#8b8f9c] font-poppins text-[10px] font-normal leading-[120%] text-center mt-5">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToSignup}
                className="text-[#e0dff5] font-poppins bg-transparent text-[10px] font-medium leading-[120%] border-none cursor-pointer transition-colors duration-300"
              >
                Sign up &gt;
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginAccount;
